import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { TrendingUp, Trophy, ShoppingBag, Gift, Users, AlertTriangle, Store, MailWarning, CalendarClock, Check } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { formatTL, formatNum, formatDate, formatDateShort } from '@/lib/format';
import { Card, TierBadge, SectionTitle, SectionLabel, StatHero, Progress, ListItem, Empty } from '@/components/shared';

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const resendVerify = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Doğrulama e-postası tekrar gönderildi');
    } catch {
      toast.error('Gönderilemedi, lütfen tekrar deneyin');
    } finally { setResending(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [me, act, notif] = await Promise.all([
          api.get('/architect/me'),
          api.get('/architect/activity'),
          api.get('/architect/notifications'),
        ]);
        setData(me.data);
        setActivity(act.data);
        const now = Date.now();
        const upcoming = (notif.data || [])
          .filter((n) => n.type === 'event' && n.eventDate && new Date(n.eventDate).getTime() >= now)
          .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
        setEvents(upcoming);
      } finally { setLoading(false); }
    })();
  }, []);

  const rsvpFromHome = async (id) => {
    try {
      const r = await api.post(`/architect/events/${id}/rsvp`);
      setEvents((prev) => prev.map((n) => (n.id === id ? { ...n, rsvped: r.data.rsvped, rsvpCount: r.data.rsvpCount } : n)));
      toast.success(r.data.rsvped ? 'Katılımın onaylandı' : 'Katılımın iptal edildi');
    } catch { toast.error('İşlem başarısız'); }
  };

  if (loading) return <div className="p-6 text-ink/40 text-sm">Yükleniyor...</div>;
  if (!data) return <Empty text="Veri bulunamadı" />;

  const { architect, summary } = data;
  const progress = summary.nextTierThreshold
    ? Math.min(100, (summary.turnoverMonth / summary.nextTierThreshold) * 100)
    : 100;

  return (
    <div className="px-5 py-6 fade-up" data-testid="architect-home">
      <p className="text-ink-soft text-sm">Merhaba,</p>
      <h1 className="font-serif text-2xl text-ink mb-5">{architect.name}</h1>

      {user && user.emailVerified === false && (
        <div data-testid="verify-banner" className="mb-4 rounded-2xl border border-gold/40 bg-gold/10 p-4 flex items-start gap-3">
          <MailWarning strokeWidth={1.5} className="w-5 h-5 text-gold shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">E-postanızı doğrulayın</p>
            <p className="text-xs text-ink-soft mt-0.5">Hesabınızın onaya hazır olması için e-posta adresinize gönderdiğimiz bağlantıyı onaylayın.</p>
            <button data-testid="verify-resend-btn" onClick={resendVerify} disabled={resending}
              className="mt-2 text-xs font-semibold text-gold hover:underline disabled:opacity-50">
              {resending ? 'Gönderiliyor...' : 'Doğrulama e-postasını tekrar gönder'}
            </button>
          </div>
        </div>
      )}

      {/* Earnings hero */}
      <Card className="p-6 mb-4">
        <div className="flex items-start justify-between">
          <StatHero
            label="Bu Ay Hakediş"
            value={formatTL(summary.earningsMonth)}
            valueClass="text-pos"
            testid="home-earnings"
            delta={`${formatTL(summary.turnoverMonth)} ciro · %${(summary.tierRate * 100).toFixed(0)} komisyon`}
            deltaTone="soft"
          />
          <TierBadge tier={summary.tier} />
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card className="p-4">
          <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center mb-3">
            <Trophy strokeWidth={1.5} className="w-4 h-4 text-gold" />
          </span>
          <div className="font-serif text-2xl text-ink tabular" data-testid="home-rank">
            {summary.rank} <span className="text-ink-faint text-base">/ {formatNum(summary.totalArchitects)}</span>
          </div>
          <div className="text-ink-soft text-xs mt-0.5">Platform sıralaması</div>
        </Card>
        <Card className="p-4">
          <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center mb-3">
            <ShoppingBag strokeWidth={1.5} className="w-4 h-4 text-gold" />
          </span>
          <div className="font-serif text-2xl text-ink tabular" data-testid="home-sales-count">{summary.salesMonth}</div>
          <div className="text-ink-soft text-xs mt-0.5">Bu ayki satış</div>
        </Card>
      </div>

      {/* Tier progress */}
      <Card className="p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-ink/70">Seviye ilerlemesi</span>
          <span className="text-xs text-ink-soft">
            {summary.nextTier ? `${summary.nextTier} seviyesine` : 'En üst seviye'}
          </span>
        </div>
        <Progress value={progress} />
        {summary.nextTier ? (
          <p className="text-xs text-ink-soft mt-2" data-testid="home-remaining">
            Bir sonraki seviye için <span className="text-gold font-medium">{formatTL(summary.remainingToNext)}</span> ciro kaldı
          </p>
        ) : (
          <p className="text-xs text-pos mt-2">Tebrikler, en yüksek seviyedesiniz.</p>
        )}
      </Card>

      {summary.protection && (
        <Card className="p-4 mb-4 flex items-start gap-3 border-gold/40" data-testid="home-protection">
          <AlertTriangle strokeWidth={1.5} className="w-5 h-5 text-gold shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-ink font-medium">Seviye koruma aktif</p>
            <p className="text-xs text-ink-soft mt-0.5">
              {summary.protection.tier} seviyeni korumak için <span className="text-gold font-medium">{formatTL(summary.protection.remaining)}</span> kaldı.
            </p>
          </div>
        </Card>
      )}

      {/* Yaklaşan Etkinlikler */}
      {events.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <SectionLabel>Yaklaşan Etkinlikler</SectionLabel>
            <button onClick={() => navigate('/app/etkinlikler')} data-testid="home-events-all"
              className="text-xs text-gold font-medium -mt-2">Tümünü gör</button>
          </div>
          <div className="space-y-2 mb-6" data-testid="home-events">
            {events.slice(0, 2).map((n) => (
              <Card key={n.id} className="overflow-hidden" data-testid={`home-event-${n.id}`}>
                {n.image && <img src={n.image} alt="" className="w-full h-28 object-cover" />}
                <div className="p-4">
                  <div className="flex items-center gap-1.5 text-xs text-gold mb-1.5">
                    <CalendarClock strokeWidth={1.5} className="w-3.5 h-3.5" /> {formatDate(n.eventDate)}
                  </div>
                  <p className="font-serif text-base text-ink leading-snug">{n.title}</p>
                  {n.body && <p className="text-xs text-ink-soft mt-1 leading-relaxed line-clamp-2">{n.body}</p>}
                  {n.rsvpEnabled && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-line">
                      <span className="flex items-center gap-1.5 text-xs text-ink-soft">
                        <Users strokeWidth={1.25} className="w-3.5 h-3.5" /> {n.rsvpCount || 0} katılımcı
                      </span>
                      <button onClick={() => rsvpFromHome(n.id)} data-testid={`home-rsvp-${n.id}`}
                        className={`inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full transition-transform active:scale-95 ${n.rsvped ? 'bg-greenx text-cream' : 'bg-ink text-ground'}`}>
                        {n.rsvped ? <><Check strokeWidth={1.5} className="w-3.5 h-3.5" /> Katılıyorsun</> : 'Katılacağım'}
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Quick links */}
      <SectionLabel>Hızlı Erişim</SectionLabel>
      <div className="space-y-2 mb-6">
        <ListItem icon={Users} iconColor="rgb(var(--gold))" title="Davet Et" subtitle="Meslektaşını davet et, prim kazan" onClick={() => navigate('/app/davet')} testid="quick-invite" chevron />
        <ListItem icon={Gift} iconColor="rgb(var(--gold))" title="Etkinlikler & Kampanyalar" subtitle="Yeni fırsatları keşfet" onClick={() => navigate('/app/etkinlikler')} testid="quick-events" chevron />
      </div>

      {/* Activity */}
      <SectionTitle>Son hareketler</SectionTitle>
      {activity.length === 0 ? (
        <Empty text="Henüz satış hareketi yok" />
      ) : (
        <div className="space-y-2" data-testid="home-activity">
          {activity.slice(0, 8).map((s) => (
            <ListItem
              key={s.id}
              icon={Store}
              iconColor="rgb(var(--ink-soft))"
              title={s.storeName}
              subtitle={`${formatDateShort(s.date)} · ${formatTL(s.basketAmount)} sepet`}
              amount={formatTL(s.commissionAmount)}
              positive
              sub={`%${(s.commissionRate * 100).toFixed(0)} komisyon`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
